import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../core/_base/crud';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, UserService } from 'src/app/core/auth/_services';
import { SalesTeamsDataSource } from 'src/app/core/timeline/_data-sources/sales-team.datasource';
import { TaskService } from 'src/app/core/timeline';
import { selectSalesTeamsPageLastQuery } from 'src/app/core/timeline/_selectors/sales-team.selectors';
import { SalesTeamsPageRequested } from 'src/app/core/timeline/_actions/sales-team.actions';
import { SalesTeamEditComponent } from '../sales-team-edit/sales-team-edit.component';
import { clone } from 'lodash-es';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { currentUser } from '../../../../../../core/auth';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { ContactRelationshipsService } from '../../../../../../core/lists';
import { DatePipe } from '@angular/common';
import { SalesTeam } from '../../../../../../core/timeline/_models/sales-team.model';
import { SalesTeamService } from '../../../../../../core/timeline/_services/sales-team.service';
import { ConfirmActionDialogComponent } from '../../../../../partials/content/crud';
import { TargetListComponent } from '../target-list/target-list.component';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-sales-teams-list',
  templateUrl: './sales-teams-list.component.html',
  styleUrls: ['./sales-teams-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesTeamsListComponent implements OnInit, OnDestroy, AfterViewInit {
  dataSource: SalesTeamsDataSource;
  displayedColumns = ['name', 'type', 'manager', 'calls_on', 'is_active', 'actions'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('managerInput') managerInput: ElementRef<HTMLInputElement>;
  filterStatus = '';
  filterCondition = '';
  lastQuery: QueryParamsModel;
  users: any[];
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  relatedIds = [];
  userFilteredIds = [];
  userObjects = [];
  toDate: FormControl;
  startDate: FormControl;
  searchForm: FormGroup;
  userIds = '';
  @ViewChild('userAutocomplete') userAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('entityAutocomplete') entityAutocomplete: GeneralAutocompleteComponent;
  size = 10;
  page = 0;
  private user: any;
  private usersChangedFirstTime = true;
  maxDate: any;
  minDate: any;
  managerFilter;
  typeFilter = '';
  callsOnFilter = '';
  @Input() fromDoctor;
  @Input() fromFacility;
  @Input() entity_id_for_task_list;
  isViewMode = false;
  loadMembers = (a) => {
    return this.userService.userAutocomplete(a).pipe(
      tap(
        (data) =>
          (data.data = data.data.map((val: any) => {
            return {
              id: val.id,
              name: val.fullName,
            };
          })),
      ),
    );
  };
  constructor(
    public dialog: MatDialog,
    public stringUtils: StringUtilsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private authService: AuthService,
    private taskService: TaskService,
    private userService: UserService,
    private fb: FormBuilder,
    private contactRelationshipsService: ContactRelationshipsService,
    private datePipe: DatePipe,
    private salesTeamService: SalesTeamService,
  ) {
    this.startDate = new FormControl(null);
    this.toDate = new FormControl(null);
    this.maxDate = new Date();
  }

  ngOnInit() {
    if (this.fromFacility) this.callsOnFilter = 'FACILITY';
    if (this.fromDoctor) this.callsOnFilter = 'DOCTOR';
    if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      this.isViewMode = true;
    }
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    /* Data load will be triggered in two cases:
    - when a pagination event occurs => this.paginator.page
    - when a sort event occurs => this.sort.sortChange
    **/
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadSalesTeamsList()))
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    // Filtration, bind to searchInput
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadSalesTeamsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    // Init DataSource
    this.dataSource = new SalesTeamsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        res.forEach((ele) => {
          if (ele && ele.call_organizer) {
            this.userFilteredIds.push(ele.call_organizer);
          }
        });
        if (this.userFilteredIds.length > 0) {
          this.taskService.getUsersbyId(this.userFilteredIds).subscribe((response) => {
            this.userObjects = [];
            this.userObjects = response.pageOfItems;
            this.cdr.detectChanges();
          });
        }
      });
    this.subscriptions.push({ id: 4, sub: entitiesSubscription });
    const lastQuerySubscription = this.store
      .pipe(select(selectSalesTeamsPageLastQuery))
      .subscribe((res) => (this.lastQuery = res));
    // Load last query from store
    this.subscriptions.push({ id: 5, sub: lastQuerySubscription });

    const userSubscription = this.store.pipe(select(currentUser), take(1)).subscribe((res) => {
      this.user = res;
      if (res.id && res.companyID) {
        this.userIds = this.user.id;
        this.loadSalesTeamsList();
      }
    });
    this.subscriptions.push({ id: 7, sub: userSubscription });

    this.searchForm = this.fb.group({
      entity: [null],
      user: [null],
    });
  }

  ngAfterViewInit(): void {
    if (this.user && this.userAutocomplete) {
      this.userAutocomplete.setValue(this.user);
      this.usersChangedFirstTime = false;
    }
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  /**
   * Load SalesTeams List
   */
  loadSalesTeamsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction !== '' ? this.sort.direction : 'desc',
      !this.sort.active || this.sort.active === '' ? 'created' : this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server

    this.store.dispatch(new SalesTeamsPageRequested({ page: queryParams }));
  }

  /**
   * Returns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};
    filter.searchData = this.searchInput.nativeElement.value;
    filter.manager_id = this.managerFilter?.id || '';
    filter.type = this.typeFilter;
    filter.calls_on = this.callsOnFilter;
    if (this.fromDoctor || this.fromFacility) {
      this.activatedRoute.params.subscribe((params) => {
        filter.entitySideObject = this.entity_id_for_task_list
          ? this.entity_id_for_task_list
          : params?.id
          ? params.id
          : '';
        return filter;
      });
    }
    return filter;
  }

  restoreState(queryParams: QueryParamsModel) {
    if (!queryParams.filter) {
      return;
    }
    if ('status' in queryParams.filter) {
      this.filterStatus = queryParams.filter.status.toString();
    }

    if (queryParams.filter.model) {
      this.searchInput.nativeElement.value = queryParams.filter.model;
    }
  }

  viewSalesTeamTarget(salesTeam: SalesTeam) {
    let entities = [];
    this.salesTeamService.getTargetList(salesTeam.id, 'sales_team_id').subscribe((result) => {
      entities = result.data;
      const dialogRef = this.dialog.open(TargetListComponent, {
        data: { entities, salesTeam },
        width: '750px',
      });
      dialogRef.afterClosed().subscribe((res) => {
        if (!res) {
          return;
        }
      });
    });
  }

  createNewSalesTeam() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60vw';
    const dialogRef = this.dialog.open(SalesTeamEditComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((res) => {
      if (res) this.loadSalesTeamsList();
    });
  }

  editSalesTeam(salesTeam) {
    if (this.isViewMode) return;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60vw';
    dialogConfig.data = { salesTeam };
    const dialogRef = this.dialog.open(SalesTeamEditComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((res) => {
      if (res) this.loadSalesTeamsList();
    });
  }

  loadPage($event: PageEvent) {
    this.page = $event.pageIndex;
    this.size = $event.pageSize;
    this.loadSalesTeamsList();
  }

  updateStatusSalesTeam(_salesTeam: SalesTeam) {
    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Activate/Deactivate Sales Team',
        description: 'Are you sure you want change sales team status?',
        waitDesciption: 'Loading...',
      },
      width: '440px',
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newObject = clone(_salesTeam);
      newObject.is_active = !_salesTeam.is_active;
      newObject.members = _salesTeam.members.map((x) => x.id);
      newObject.manager_id = _salesTeam.manager.id;
      delete newObject.manager;
      this.salesTeamService.updateSalesTeam(newObject, _salesTeam.id).subscribe((result) => {
        if (result) {
          this.layoutUtilsService.showActionNotification(
            'Sales team status updated successfully',
            MessageType.Update,
            3000,
            false,
            false,
          );
          this.loadSalesTeamsList();
        }
      });
    });
  }

  deleteSalesTeam(_salesTeam: SalesTeam) {
    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Delete Sales Team',
        description: 'Are you sure you want to delete sales team?',
        waitDesciption: 'Loading...',
      },
      width: '440px',
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.salesTeamService.deleteSalesTeam(_salesTeam.id).subscribe(() => {
        this.layoutUtilsService.showActionNotification(
          'Sales team deleted successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
        this.loadSalesTeamsList();
      });
    });
  }
  onOrganizerChange(val) {
    this.managerFilter = val;
    this.paginator.firstPage();
    this.loadSalesTeamsList();
  }
}
